import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Chip,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Divider,
  Stack,
  Container,
  Fade,
  Tooltip,
  Badge,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  AccountBalance,
  Settings as SettingsIcon,
  LocalHospital as LocalHospitalIcon,
  Home as HomeIcon,
  Receipt as ReceiptIcon,
  Badge as BadgeIcon,
  CheckCircle as CheckCircleIcon,
  PauseCircle as PauseCircleIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { employeeService } from '../services/employeeService';
import { departmentService } from '../services/departmentService';
import { Employee, Department } from '../types';
import { formatDate } from '../utils/dateUtils';
import { formatCurrency } from '../utils/currencyutils';
import { exportUtils } from '../utils/exportUtils';
import EmployeeDeductions from '../components/EmployeeDeductions';

const Employees: React.FC = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [currentEmployee, setCurrentEmployee] = useState<Partial<Employee>>({});
  const [openDeductionsDialog, setOpenDeductionsDialog] = useState(false);
  const [selectedEmployeeForDeductions, setSelectedEmployeeForDeductions] = useState<Employee | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const employeesData = await employeeService.getAll();
        const departmentsData = await departmentService.getAll();
        setEmployees(employeesData);
        setDepartments(departmentsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSearch = async () => {
    if (searchTerm.trim() === '') {
      const employeesData = await employeeService.getAll();
      setEmployees(employeesData);
    } else {
      const results = await employeeService.search(searchTerm);
      setEmployees(results);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenAddDialog = () => {
    setDialogMode('add');
    setCurrentEmployee({
      status: 'active',
    });
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (employee: Employee) => {
    setDialogMode('edit');
    setCurrentEmployee({ ...employee });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentEmployee({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentEmployee(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setCurrentEmployee(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveEmployee = async () => {
    setLoading(true);
    try {
      // Validate required fields
      if (!currentEmployee.first_name || !currentEmployee.last_name || !currentEmployee.email) {
        setSnackbar({
          open: true,
          message: 'Please fill in all required fields (First Name, Last Name, Email)',
          severity: 'error'
        });
        setLoading(false);
        return;
      }

      let result;
      if (dialogMode === 'add') {
        result = await employeeService.create(currentEmployee as Omit<Employee, 'id' | 'created_at' | 'updated_at'>);
        if (result) {
          setSnackbar({
            open: true,
            message: 'Successfully Data Save',
            severity: 'success'
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Failed to create employee. Please check the console for errors.',
            severity: 'error'
          });
          setLoading(false);
          return;
        }
      } else {
        if (currentEmployee.id) {
          console.log('Updating employee with data:', currentEmployee);
          result = await employeeService.update(currentEmployee.id, currentEmployee);
          if (result) {
            setSnackbar({
              open: true,
              message: 'Successfully Data Save',
              severity: 'success'
            });
            console.log('Employee updated successfully:', result);
          } else {
            setSnackbar({
              open: true,
              message: 'Failed to update employee. Please check the console for errors.',
              severity: 'error'
            });
            setLoading(false);
            return;
          }
        } else {
          setSnackbar({
            open: true,
            message: 'Employee ID is missing. Cannot update.',
            severity: 'error'
          });
          setLoading(false);
          return;
        }
      }

      // Refresh employee list
      const employeesData = await employeeService.getAll();
      setEmployees(employeesData);
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving employee:', error);
      setSnackbar({
        open: true,
        message: `Error saving employee: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmployee = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await employeeService.delete(id);
        // Refresh employee list
        const employeesData = await employeeService.getAll();
        setEmployees(employeesData);
        setSnackbar({
          open: true,
          message: 'Employee deleted successfully',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error deleting employee:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to delete employee. Please try again.';
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error'
        });
      }
    }
  };

  const handleOpenDeductionsDialog = (employee: Employee) => {
    setSelectedEmployeeForDeductions(employee);
    setOpenDeductionsDialog(true);
  };

  const handleCloseDeductionsDialog = () => {
    setOpenDeductionsDialog(false);
    setSelectedEmployeeForDeductions(null);
  };

  const handleExportEmployees = async () => {
    try {
      setLoading(true);
      const activeEmployees = await employeeService.getActiveEmployeesForExport();
      
      if (activeEmployees.length === 0) {
        setSnackbar({
          open: true,
          message: 'No active employees found to export',
          severity: 'warning'
        });
        return;
      }

      await exportUtils.exportActiveEmployees(activeEmployees);
      
      setSnackbar({
        open: true,
        message: `Successfully exported ${activeEmployees.length} active employees`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error exporting employees:', error);
      setSnackbar({
        open: true,
        message: 'Failed to export employee data. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter employees based on search term and exclude admin accounts
  const filteredEmployees = employees.filter(employee => {
    // Hide admin accounts (System Administrator position or admin email)
    const isAdmin = employee.position.toLowerCase().includes('system administrator') || 
                   employee.position.toLowerCase().includes('admin') ||
                   employee.email.toLowerCase().includes('admin@');
    
    if (isAdmin) return false;
    
    // Apply search filter
    return employee.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           employee.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
           employee.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
           employee.department.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Paginate employees
  const paginatedEmployees = filteredEmployees.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Fade in={loading}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '60vh',
            gap: 2
          }}>
            <CircularProgress size={60} thickness={4} sx={{ color: '#2563eb' }} />
            <Typography variant="h6" color="text.secondary">
              Loading employees...
            </Typography>
          </Box>
        </Fade>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Card
        sx={{
          background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 50%, #1e40af 100%)',
          color: 'white',
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(37, 99, 235, 0.2), 0 8px 16px rgba(124, 58, 237, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
            opacity: 1,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.08"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4,
          },
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                }}
              >
                <PeopleIcon sx={{ fontSize: 40, color: 'white' }} />
              </Avatar>
              <Box>
                <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
                  Employee Management
                </Typography>
                <Typography variant="body1" sx={{ fontSize: '1.1rem', color: 'rgba(255, 255, 255, 0.9)' }}>
                  Manage your organization's workforce and employee information
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 1 }}>
                  Total Employees: {employees.length}
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={2}>
              <Tooltip title="Export active employees to CSV">
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportEmployees}
                  disabled={loading}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      transform: 'translateY(-2px)',
                    },
                    '&:disabled': {
                      color: 'rgba(255, 255, 255, 0.5)',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Export CSV
                </Button>
              </Tooltip>
              <Tooltip title="Register new employee account">
                <Button
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => navigate('/register')}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Register Employee
                </Button>
              </Tooltip>
              <Tooltip title="Add new employee record">
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddDialog}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Add Employee
                </Button>
              </Tooltip>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {/* Search Bar */}
      <Card sx={{ mb: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: '#2563eb' }}>
              <SearchIcon />
            </Avatar>
          }
          title="Search Employees"
          subheader="Find employees by name, email, position, or department"
        />
        <Divider />
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              label="Search Employees"
              variant="outlined"
              size="medium"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Enter name, email, position, or department..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#2563eb',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2563eb',
                  },
                },
              }}
            />
            <Tooltip title="Search employees">
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{
                  minWidth: 120,
                  bgcolor: '#2563eb',
                  '&:hover': {
                    bgcolor: '#1d4ed8',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Search
              </Button>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>

      {/* Employees Table */}
      <Card sx={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: '#2563eb' }}>
              <PeopleIcon />
            </Avatar>
          }
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">Employee Records</Typography>
              <Badge badgeContent={filteredEmployees.length} color="primary" />
            </Box>
          }
          subheader="Complete employee information and management"
        />
        <Divider />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Employee</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Position & Department</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Government IDs</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Employment</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedEmployees.map((employee, index) => (
                <TableRow 
                  key={employee.id}
                  sx={{
                    '&:hover': {
                      bgcolor: '#f8fafc',
                      transform: 'scale(1.001)',
                    },
                    transition: 'all 0.2s ease',
                    borderLeft: index % 2 === 0 ? '4px solid #e5e7eb' : '4px solid transparent',
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: '#2563eb',
                          width: 40,
                          height: 40,
                          fontSize: '0.875rem',
                        }}
                      >
                        {employee.first_name.charAt(0)}{employee.last_name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {`${employee.first_name} ${employee.last_name}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {employee.id.slice(0, 8)}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Chip
                        icon={<WorkIcon />}
                        label={employee.position}
                        size="small"
                        sx={{ mb: 1, bgcolor: '#eff6ff', color: '#1d4ed8' }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        <BusinessIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                        {employee.department}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <EmailIcon sx={{ fontSize: 14, mr: 0.5, color: '#6b7280' }} />
                        {employee.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                        <PhoneIcon sx={{ fontSize: 14, mr: 0.5, color: '#6b7280' }} />
                        {employee.phone || 'N/A'}
                      </Typography>
                      {employee.address && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                          <LocationIcon sx={{ fontSize: 12, mr: 0.5, color: '#6b7280' }} />
                          {employee.address.length > 30 ? `${employee.address.substring(0, 30)}...` : employee.address}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        SSS: {employee.sss_number || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        PhilHealth: {employee.philhealth_number || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pag-IBIG: {employee.pagibig_number || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        TIN: {employee.tin_number || 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Chip
                        icon={<CalendarIcon />}
                        label={formatDate(employee.hire_date)}
                        size="small"
                        sx={{ mb: 1, bgcolor: '#f0fdf4', color: '#166534' }}
                      />
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600, color: '#6b7280' }}>
                        <WorkIcon sx={{ fontSize: 14, mr: 0.5, color: '#6b7280' }} />
                        {employee.employment_type || 'Regular'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={employee.status.toUpperCase()}
                      color={employee.status === 'active' ? 'success' : 'error'}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        '& .MuiChip-label': {
                          px: 2,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Edit employee">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditDialog(employee)}
                          sx={{
                            color: '#2563eb',
                            '&:hover': {
                              bgcolor: '#eff6ff',
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete employee">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteEmployee(employee.id)}
                          sx={{
                            color: '#dc2626',
                            '&:hover': {
                              bgcolor: '#fef2f2',
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Manage deductions">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDeductionsDialog(employee)}
                          sx={{
                            color: '#059669',
                            '&:hover': {
                              bgcolor: '#f0fdf4',
                              transform: 'scale(1.1)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <MoneyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Divider />
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredEmployees.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              '& .MuiTablePagination-toolbar': {
                bgcolor: '#f8fafc',
              },
            }}
          />
        </TableContainer>
      </Card>

      {/* Add/Edit Employee Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {

            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#2563eb', 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          py: 3
        }}>
          <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
            {dialogMode === 'add' ? <PersonAddIcon /> : <EditIcon />}
          </Avatar>
          <Box>
            <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
              {dialogMode === 'add' ? 'Add New Employee' : 'Edit Employee'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              {dialogMode === 'add' ? 'Enter employee information below' : 'Update employee details'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Personal Information Section */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar sx={{ bgcolor: '#eff6ff', color: '#2563eb', width: 32, height: 32 }}>
                  <PeopleIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
                  Personal Information
                </Typography>
              </Box>
            </Grid>
            
            {/* Employee ID - Show only in edit mode */}
            {dialogMode === 'edit' && currentEmployee.id && (
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  name="id"
                  label="Employee ID"
                  value={currentEmployee.id || ''}
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    startAdornment: <BadgeIcon sx={{ color: '#6b7280', mr: 1 }} />,
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      backgroundColor: '#f9fafb',
                      color: '#6b7280',
                    },
                  }}
                />
              </Grid>
            )}
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="first_name"
                label="First Name"
                value={currentEmployee.first_name || ''}
                onChange={handleInputChange}
                required
                fullWidth
                InputProps={{
                  startAdornment: <PeopleIcon sx={{ color: '#6b7280', mr: 1 }} />,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="last_name"
                label="Last Name"
                value={currentEmployee.last_name || ''}
                onChange={handleInputChange}
                required
                fullWidth
                InputProps={{
                  startAdornment: <PeopleIcon sx={{ color: '#6b7280', mr: 1 }} />,
                }}
              />
            </Grid>
            
            {/* Contact Information Section */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 2 }}>
                <Avatar sx={{ bgcolor: '#f0fdf4', color: '#059669', width: 32, height: 32 }}>
                  <EmailIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
                  Contact Information
                </Typography>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="email"
                label="Email"
                type="email"
                value={currentEmployee.email || ''}
                onChange={handleInputChange}
                required
                fullWidth
                InputProps={{
                  startAdornment: <EmailIcon sx={{ color: '#6b7280', mr: 1 }} />,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="phone"
                label="Phone"
                value={currentEmployee.phone || ''}
                onChange={handleInputChange}
                required
                fullWidth
                InputProps={{
                  startAdornment: <PhoneIcon sx={{ color: '#6b7280', mr: 1 }} />,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                name="address"
                label="Address"
                value={currentEmployee.address || ''}
                onChange={handleInputChange}
                multiline
                rows={2}
                fullWidth
                InputProps={{
                  startAdornment: <LocationIcon sx={{ color: '#6b7280', mr: 1, alignSelf: 'flex-start', mt: 1 }} />,
                }}
              />
            </Grid>
            {/* Employment Information Section */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 2 }}>
                <Avatar sx={{ bgcolor: '#fef3c7', color: '#d97706', width: 32, height: 32 }}>
                  <WorkIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
                  Employment Information
                </Typography>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="position"
                label="Position"
                value={currentEmployee.position || ''}
                onChange={handleInputChange}
                required
                fullWidth
                InputProps={{
                  startAdornment: <WorkIcon sx={{ color: '#6b7280', mr: 1 }} />,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  name="department"
                  value={currentEmployee.department || ''}
                  onChange={handleSelectChange}
                  label="Department"
                  required
                  startAdornment={<BusinessIcon sx={{ color: '#6b7280', mr: 1 }} />}
                >
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.name}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select
                  name="gender"
                  value={currentEmployee.gender || 'male'}
                  onChange={handleSelectChange}
                  label="Gender"
                  required
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="hire_date"
                label="Hire Date"
                type="date"
                value={currentEmployee.hire_date || ''}
                onChange={handleInputChange}
                required
                InputLabelProps={{ shrink: true }}
                fullWidth
                InputProps={{
                  startAdornment: <CalendarIcon sx={{ color: '#6b7280', mr: 1 }} />,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="salary"
                label="Salary"
                type="number"
                value={currentEmployee.salary || ''}
                onChange={handleInputChange}
                required
                fullWidth
                InputProps={{
                   startAdornment: <MoneyIcon sx={{ color: '#6b7280', mr: 1 }} />,
                }}
              />
            </Grid>
            {/* Financial Information Section */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 2 }}>
                <Avatar sx={{ bgcolor: '#ecfdf5', color: '#059669', width: 32, height: 32 }}>
                   <AccountBalance fontSize="small" />
                </Avatar>
                <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
                  Financial Information
                </Typography>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="bank_account"
                label="Bank Account"
                value={currentEmployee.bank_account || ''}
                onChange={handleInputChange}
                fullWidth
                InputProps={{
                   startAdornment: <AccountBalance sx={{ color: '#6b7280', mr: 1 }} />,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Employment Type</InputLabel>
                <Select
                  name="employment_type"
                  value={currentEmployee.employment_type || 'regular'}
                  onChange={handleSelectChange}
                  label="Employment Type"
                  required
                  startAdornment={<WorkIcon sx={{ color: '#6b7280', mr: 1 }} />}
                >
                  <MenuItem value="regular">Regular</MenuItem>
                  <MenuItem value="contractual">Contractual</MenuItem>
                  <MenuItem value="part-time">Part-time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="hourly_rate"
                label="Hourly Rate (Optional)"
                type="number"
                value={currentEmployee.hourly_rate || ''}
                onChange={handleInputChange}
                fullWidth
                InputProps={{
                  startAdornment: <MoneyIcon sx={{ color: '#6b7280', mr: 1 }} />,
                }}
              />
            </Grid>
            
            {/* Government IDs Section */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 2 }}>
                <Avatar sx={{ bgcolor: '#fef2f2', color: '#dc2626', width: 32, height: 32 }}>
                   <BadgeIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
                  Government IDs
                </Typography>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="sss_number"
                label="SSS Number"
                value={currentEmployee.sss_number || ''}
                onChange={handleInputChange}
                placeholder="XX-XXXXXXX-X"
                fullWidth
                InputProps={{
                   startAdornment: <BadgeIcon sx={{ color: '#6b7280', mr: 1 }} />,
                 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="philhealth_number"
                label="PhilHealth Number"
                value={currentEmployee.philhealth_number || ''}
                onChange={handleInputChange}
                placeholder="XX-XXXXXXXXX-X"
                fullWidth
                InputProps={{
                   startAdornment: <LocalHospitalIcon sx={{ color: '#6b7280', mr: 1 }} />,
                 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="pagibig_number"
                label="Pag-IBIG Number"
                value={currentEmployee.pagibig_number || ''}
                onChange={handleInputChange}
                placeholder="XXXX-XXXX-XXXX"
                fullWidth
                InputProps={{
                   startAdornment: <HomeIcon sx={{ color: '#6b7280', mr: 1 }} />,
                 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="tin_number"
                label="TIN Number"
                value={currentEmployee.tin_number || ''}
                onChange={handleInputChange}
                placeholder="XXX-XXX-XXX-XXX"
                fullWidth
                InputProps={{
                   startAdornment: <ReceiptIcon sx={{ color: '#6b7280', mr: 1 }} />,
                 }}
              />
            </Grid>
            
            {/* Payroll Deduction Preferences Section */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 2 }}>
                <Avatar sx={{ bgcolor: '#f3e8ff', color: '#7c3aed', width: 32, height: 32 }}>
                  <SettingsIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
                  Payroll Deduction Preferences
                </Typography>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Card sx={{ p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="deduct_sss"
                          checked={currentEmployee.deduct_sss !== false}
                          onChange={(e) => setCurrentEmployee(prev => ({
                            ...prev,
                            deduct_sss: e.target.checked
                          }))}
                          sx={{ color: '#2563eb' }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <BadgeIcon fontSize="small" sx={{ color: '#6b7280' }} />
                           <Typography variant="body2">Deduct SSS</Typography>
                         </Box>
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="deduct_philhealth"
                          checked={currentEmployee.deduct_philhealth !== false}
                          onChange={(e) => setCurrentEmployee(prev => ({
                            ...prev,
                            deduct_philhealth: e.target.checked
                          }))}
                          sx={{ color: '#059669' }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <LocalHospitalIcon fontSize="small" sx={{ color: '#6b7280' }} />
                           <Typography variant="body2">Deduct PhilHealth</Typography>
                         </Box>
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="deduct_pagibig"
                          checked={currentEmployee.deduct_pagibig !== false}
                          onChange={(e) => setCurrentEmployee(prev => ({
                            ...prev,
                            deduct_pagibig: e.target.checked
                          }))}
                          sx={{ color: '#d97706' }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <HomeIcon fontSize="small" sx={{ color: '#6b7280' }} />
                           <Typography variant="body2">Deduct Pag-IBIG</Typography>
                         </Box>
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="deduct_tax"
                          checked={currentEmployee.deduct_tax !== false}
                          onChange={(e) => setCurrentEmployee(prev => ({
                            ...prev,
                            deduct_tax: e.target.checked
                          }))}
                          sx={{ color: '#dc2626' }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                           <ReceiptIcon fontSize="small" sx={{ color: '#6b7280' }} />
                           <Typography variant="body2">Deduct Tax</Typography>
                         </Box>
                      }
                    />
                  </Grid>
                </Grid>
              </Card>
            </Grid>
            
            {/* Status Section */}
            <Grid size={{ xs: 12 }}>
               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 2 }}>
                 <Avatar sx={{ bgcolor: '#fef3c7', color: '#d97706', width: 32, height: 32 }}>
                   <CheckCircleIcon fontSize="small" />
                 </Avatar>
                 <Typography variant="h6" sx={{ color: '#374151', fontWeight: 600 }}>
                   Employee Status
                 </Typography>
               </Box>
             </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={currentEmployee.status || 'active'}
                  onChange={handleSelectChange}
                  label="Status"
                  required
                  startAdornment={<CheckCircleIcon sx={{ color: '#6b7280', mr: 1 }} />}
                >
                  <MenuItem value="active">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon fontSize="small" sx={{ color: '#059669' }} />
                      Active
                    </Box>
                  </MenuItem>
                  <MenuItem value="inactive">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PauseCircleIcon fontSize="small" sx={{ color: '#d97706' }} />
                      Inactive
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button 
            onClick={handleCloseDialog} 
            disabled={loading}
            sx={{ 
              color: '#6b7280',
              '&:hover': {
                bgcolor: '#f3f4f6'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEmployee} 
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{
              bgcolor: '#2563eb',
              '&:hover': {
                bgcolor: '#1d4ed8'
              },
              px: 4,
              py: 1.5
            }}
          >
            {loading ? 'Saving...' : 'Save Employee'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Employee Deductions Dialog */}
      <Dialog 
        open={openDeductionsDialog} 
        onClose={handleCloseDeductionsDialog} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: '#059669', 
          color: 'white', 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          py: 3
        }}>
          <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
            <MoneyIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
              Manage Employee Deductions
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              {selectedEmployeeForDeductions ? 
                `${selectedEmployeeForDeductions.first_name} ${selectedEmployeeForDeductions.last_name}` : 
                'Employee Deductions'
              }
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedEmployeeForDeductions && (
            <EmployeeDeductions 
               employeeId={selectedEmployeeForDeductions.id}
             />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button 
            onClick={handleCloseDeductionsDialog}
            sx={{ 
              color: '#6b7280',
              '&:hover': {
                bgcolor: '#f3f4f6'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Employees;